import React, { useMemo, useState } from "react";
import {
  Box, Breadcrumbs, Button, Card, Chip, CircularProgress, IconButton, InputAdornment,
  Link as MLink, List, ListItemButton, ListItemText, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { CiSearch } from "react-icons/ci";
import { Link, useNavigate } from "react-router-dom";
import { useGetProgramQuery, useCloneProgramMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { PageSurface, LegacyTokens, TypeScale } from "../../../theme/b13Tokens";

/**
 * Batch 61 §5 — Program Templates settings page.
 *
 * Promotes the pre-existing TemplatesDrawer to a full page under
 * /aba-therapy/settings/templates. Filters programs by
 * `is_template = 1` (or legacy studentId IS NULL fallback) — same
 * shape as the drawer, but rendered as a page so it can live in the
 * new Settings sub-group.
 *
 * "Create program from template" clones with asTemplate=false and
 * lands the operator on the new library entry for editing. The
 * "Mark as template" action lives on Program Detail — see that page
 * for the cloning semantics (creates a template copy, leaves the
 * original student's instance intact).
 */
export default function TemplatesPage() {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();

  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 300);
  const { data, isLoading } = useGetProgramQuery({ searchText: debounced, page: 1, perPage: 100 });
  const [cloneProgram] = useCloneProgramMutation();

  const rows: any[] = useMemo(() => {
    const raw = Array.isArray(data?.data) ? (data!.data as any[]) : [];
    return raw
      .filter((p) => Number(p.is_template) === 1 || p.is_template === true || p.studentId == null)
      .map((p) => {
        let tags: any = p.tags;
        if (typeof tags === "string" && tags.length) {
          try { tags = JSON.parse(tags); } catch { tags = []; }
        }
        return { ...p, tags: Array.isArray(tags) ? tags : [] };
      });
  }, [data]);

  const handleCreateFromTemplate = async (tpl: any) => {
    try {
      const res = await cloneProgram({
        programId: tpl.id,
        asTemplate: false,
        newName: tpl.programName + " (from template)",
      }).unwrap();
      if (res.status) {
        showMessage("Program created from template. Opening for edit.");
        const newId = (res.data as any)?.id;
        if (newId) navigate(`/aba-therapy/programs/edit/${newId}`);
      } else {
        showErrorMessage(res.message || "Failed to create program from template.");
      }
    } catch {
      showErrorMessage("Failed to create program from template.");
    }
  };

  return (
    <Box sx={{ ...PageSurface, p: { xs: 2, md: 3 } }}>
      <Breadcrumbs sx={{ mb: 1.5, fontSize: 12 }} separator="/">
        <MLink underline="hover" component={Link} to="/aba-therapy/programs" sx={{ color: LegacyTokens.ink3 }}>ABA Therapy</MLink>
        <MLink underline="hover" component={Link} to="/aba-therapy/settings/templates" sx={{ color: LegacyTokens.ink3 }}>Settings</MLink>
        <Typography sx={{ fontSize: 12, color: LegacyTokens.ink }}>Program Templates</Typography>
      </Breadcrumbs>

      <Box sx={{ mb: 3 }}>
        <Typography component="h1" sx={{ ...TypeScale.pageTitle, lineHeight: 1.15 }}>
          Program Templates
        </Typography>
        <Typography sx={{ ...TypeScale.caption, mt: 0.5, maxWidth: 720 }}>
          Library entries with no student assigned. Duplicate one to author a new program for a specific learner.
        </Typography>
      </Box>

      <Card variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: LegacyTokens.line, mb: 2 }}>
        <TextField
          fullWidth size="small" placeholder="Search templates"
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><CiSearch /></InputAdornment> }}
        />
      </Card>

      <Card variant="outlined" sx={{ borderRadius: 2, borderColor: LegacyTokens.line }}>
        {isLoading ? (
          <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}><CircularProgress size={24} /></Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No templates yet. Open a program and choose "Mark as template" to add one here.
            </Typography>
            <Button variant="outlined" size="small" onClick={() => navigate("/aba-therapy/programs/new")}>
              New Program
            </Button>
          </Box>
        ) : (
          <List dense sx={{ overflow: "auto" }}>
            {rows.map((tpl) => (
              <ListItemButton key={tpl.id} sx={{ alignItems: "flex-start", py: 1.5 }}>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="body2" fontWeight={700}>{tpl.programName}</Typography>
                      {(tpl.treatmentAreaName || tpl.category) && <Chip size="small" variant="outlined" label={tpl.treatmentAreaName || tpl.category} />}
                    </Stack>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {tpl.targetCount ?? 0} target(s) · {tpl.authorName ? `by ${tpl.authorName}` : "unknown author"}
                      </Typography>
                      {/* Batch 14 Step 3b — render from the pivot join
                         (tagsWithColour) so a Settings-side rename or
                         colour change flows here on the next fetch.
                         Falls back to legacy tags string[] only when
                         the endpoint hasn't populated the field.
                         Colour comes through as the Chip's outline
                         + text; neutral-styled when null. */}
                      {(() => {
                        const rich: Array<{ tagId: number; name: string; colour: string | null }> =
                          Array.isArray(tpl.tagsWithColour) && tpl.tagsWithColour.length > 0
                            ? tpl.tagsWithColour
                            : (Array.isArray(tpl.tags)
                                ? tpl.tags.map((t: string, i: number) => ({ tagId: -(i + 1), name: t, colour: null }))
                                : []);
                        if (rich.length === 0) return null;
                        return (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                            {rich.slice(0, 4).map((t) => (
                              <Chip
                                key={t.tagId}
                                label={t.name}
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: 20,
                                  ...(t.colour
                                    ? { borderColor: t.colour, color: t.colour }
                                    : {}),
                                }}
                              />
                            ))}
                          </Stack>
                        );
                      })()}
                    </Box>
                  }
                />
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Preview">
                    <IconButton size="small" onClick={() => navigate(`/aba-therapy/programs/${tpl.id}`)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Create program from this template">
                    <IconButton size="small" onClick={() => handleCreateFromTemplate(tpl)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </ListItemButton>
            ))}
          </List>
        )}
      </Card>
    </Box>
  );
}
